<template>
  <div class="flex flex-col gap-3" dir="rtl">
    <div class="flex flex-col gap-0.5">
      <span class="font-medium">משימות לכל תור</span>
      <span class="text-xs text-surface-500 leading-relaxed">
        רשימה קבועה שתופיע בכל תור של השירות — הכנה לפני הטיפול ופעולות אחריו. משימה שנקבעת לימים אחרי התור נפתחת כמשימת המשך ללקוח.
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-[13px] font-semibold text-surface-600">לפני הטיפול</span>
      <div v-for="(_, i) in checklist.before" :key="`b${i}`" class="flex gap-2 items-center">
        <InputText v-model="checklist.before[i]" class="flex-1" size="small" placeholder="לדוגמה: להכין קרטין וכפפות" maxlength="200" />
        <button type="button" class="icon-btn" aria-label="הסרה" @click="checklist.before.splice(i, 1)"><i class="pi pi-times text-xs" /></button>
      </div>
      <button type="button" class="add-link" @click="checklist.before.push('')">+ הוספת משימת הכנה</button>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-[13px] font-semibold text-surface-600">אחרי הטיפול</span>
      <div v-for="(item, i) in checklist.after" :key="`a${i}`" class="after-item">
        <div class="flex gap-2 items-center">
          <InputText v-model="item.title" class="flex-1 min-w-0" size="small" placeholder="לדוגמה: בדיקת שיער ללקוחה" maxlength="200" />
          <button type="button" class="icon-btn" aria-label="הסרה" @click="checklist.after.splice(i, 1)"><i class="pi pi-times text-xs" /></button>
        </div>
        <div class="timing-row" role="radiogroup" :aria-label="`מתי — ${item.title || 'משימה'}`">
          <button
            v-for="option in timingOptions(item.daysAfter)"
            :key="option.days"
            type="button"
            role="radio"
            :aria-checked="(item.daysAfter ?? 0) === option.days"
            class="timing-chip"
            :class="{ active: (item.daysAfter ?? 0) === option.days }"
            @click="item.daysAfter = option.days"
          >{{ option.label }}</button>
        </div>
      </div>
      <button type="button" class="add-link" @click="checklist.after.push({ title: '', daysAfter: 0 })">+ הוספת משימה אחרי הטיפול</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IServiceTaskChecklist } from '@torim-app/types';

defineProps<{ checklist: IServiceTaskChecklist }>();

const PRESETS = [
  { days: 0, label: 'באותו יום' },
  { days: 1, label: 'למחרת' },
  { days: 7, label: 'אחרי שבוע' },
  { days: 14, label: 'אחרי שבועיים' },
  { days: 30, label: 'אחרי חודש' },
];

/** The presets, plus the item's own value when it was saved with another number of days. */
const timingOptions = (daysAfter?: number) => {
  const days = daysAfter ?? 0;
  return PRESETS.some(p => p.days === days)
    ? PRESETS
    : [...PRESETS, { days, label: `אחרי ${days} ימים` }].sort((a, b) => a.days - b.days);
};
</script>

<style scoped lang="scss">
.icon-btn {
  @apply w-8 h-8 flex-none rounded-lg text-surface-500 hover:bg-surface-100 flex items-center justify-center;
}

.after-item {
  @apply flex flex-col gap-2 p-2.5 rounded-xl bg-surface-50 border border-surface-200;
}

.timing-row {
  @apply flex gap-1.5 overflow-x-auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.timing-chip {
  @apply flex-none whitespace-nowrap h-8 px-3 rounded-full border border-surface-200 bg-white text-[13px] text-surface-700;

  &.active {
    @apply border-primary bg-primary-50 text-primary-700 font-semibold;
  }
}

.add-link {
  @apply self-start text-sm text-primary-700 font-medium;
}
</style>
